/** Dias da semana no backend FAQ (1 = segunda … 7 = domingo). */
export const FAQ_WEEKDAYS = [
  { value: 1, labelKey: "agentsListPage.faqWeekdayMon" },
  { value: 2, labelKey: "agentsListPage.faqWeekdayTue" },
  { value: 3, labelKey: "agentsListPage.faqWeekdayWed" },
  { value: 4, labelKey: "agentsListPage.faqWeekdayThu" },
  { value: 5, labelKey: "agentsListPage.faqWeekdayFri" },
  { value: 6, labelKey: "agentsListPage.faqWeekdaySat" },
  { value: 7, labelKey: "agentsListPage.faqWeekdaySun" },
];

export const FAQ_TIMEZONE_OPTIONS = [
  { value: "America/Sao_Paulo", labelKey: "agentsListPage.faqTzSaoPaulo" },
  { value: "America/Manaus", labelKey: "agentsListPage.faqTzManaus" },
  { value: "America/Fortaleza", labelKey: "agentsListPage.faqTzFortaleza" },
  { value: "America/Recife", labelKey: "agentsListPage.faqTzRecife" },
  { value: "America/Cuiaba", labelKey: "agentsListPage.faqTzCuiaba" },
  { value: "America/Porto_Velho", labelKey: "agentsListPage.faqTzPortoVelho" },
  { value: "America/Bahia", labelKey: "agentsListPage.faqTzBahia" },
  { value: "America/Belem", labelKey: "agentsListPage.faqTzBelem" },
  { value: "America/Rio_Branco", labelKey: "agentsListPage.faqTzRioBranco" },
];

export const DEFAULT_FAQ_BUSINESS_HOURS = {
  timezone: "America/Sao_Paulo",
  weekdays: [1, 2, 3, 4, 5],
  start: "09:00",
  end: "18:00",
};

/**
 * Perfis de busca do FAQ. O perfil "balanced" reproduz exatamente os valores
 * usados pelo backend, então um agente sem `retrieval` salvo responde igual.
 */
export const FAQ_RETRIEVAL_PROFILES = {
  precise: {
    profile: "precise",
    top_k: 6,
    min_lexical_score: 3,
    labelKey: "agentsListPage.faqRetrievalProfilePrecise",
    hintKey: "agentsListPage.faqRetrievalProfilePreciseHint",
  },
  balanced: {
    profile: "balanced",
    top_k: 10,
    min_lexical_score: 2,
    labelKey: "agentsListPage.faqRetrievalProfileBalanced",
    hintKey: "agentsListPage.faqRetrievalProfileBalancedHint",
  },
  broad: {
    profile: "broad",
    top_k: 14,
    min_lexical_score: 1.5,
    labelKey: "agentsListPage.faqRetrievalProfileBroad",
    hintKey: "agentsListPage.faqRetrievalProfileBroadHint",
  },
};

/** Valores efetivos quando o agente não tem `retrieval` salvo. */
export const DEFAULT_FAQ_RETRIEVAL = {
  profile: "balanced",
  top_k: 10,
  min_lexical_score: 2,
  alias_groups: [],
  alias_groups_mode: "merge",
};

export function isFaqBusinessHoursEnabled(skillConfig) {
  const bh = skillConfig?.business_hours;
  return Boolean(bh && typeof bh === "object" && String(bh.timezone || "").trim());
}

export function normalizeFaqBusinessHours(raw) {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_FAQ_BUSINESS_HOURS };
  const weekdays = Array.isArray(raw.weekdays)
    ? raw.weekdays.map((n) => Number(n)).filter((n) => n >= 1 && n <= 7)
    : [...DEFAULT_FAQ_BUSINESS_HOURS.weekdays];
  return {
    timezone:
      typeof raw.timezone === "string" && raw.timezone.trim()
        ? raw.timezone.trim()
        : DEFAULT_FAQ_BUSINESS_HOURS.timezone,
    weekdays: weekdays.length ? weekdays : [...DEFAULT_FAQ_BUSINESS_HOURS.weekdays],
    start: typeof raw.start === "string" && raw.start.trim() ? raw.start.trim() : DEFAULT_FAQ_BUSINESS_HOURS.start,
    end: typeof raw.end === "string" && raw.end.trim() ? raw.end.trim() : DEFAULT_FAQ_BUSINESS_HOURS.end,
  };
}

export function getFaqOutsideHoursMessage(skillConfig) {
  const msg = skillConfig?.outside_hours_message ?? skillConfig?.out_of_hours_message;
  return typeof msg === "string" ? msg : "";
}

/** Normaliza HH:MM para input type=time. */
export function toTimeInputValue(hm) {
  const s = String(hm || "").trim();
  if (!s) return "09:00";
  const parts = s.split(":");
  const h = String(parseInt(parts[0], 10) || 0).padStart(2, "0");
  const m = String(parseInt(parts[1] ?? "0", 10) || 0).padStart(2, "0");
  return `${h}:${m}`;
}

export const FAQ_DEFAULT_MESSAGES = {
  greeting_message: "Olá! Sou o assistente virtual. Em que posso ajudar?",
  greeting_message_morning: "Bom dia! Sou o assistente virtual. Em que posso ajudar?",
  greeting_message_afternoon: "Boa tarde! Sou o assistente virtual. Em que posso ajudar?",
  greeting_message_evening: "Boa noite! Sou o assistente virtual. Como posso ajudar você hoje?",
  assistant_identity: "",
  no_match_reply:
    "Não encontrei isso nas informações que tenho aqui. Quer tentar de outro jeito (outras palavras) ou prefere que eu encaminhe para um atendente humano?",
  transfer_offer_hint: "",
};

/**
 * Lê retrieval efetivo para exibição (não marca dirty se ausente no save).
 */
export function getEffectiveFaqRetrieval(skillConfig = {}) {
  const raw = skillConfig?.retrieval && typeof skillConfig.retrieval === "object" ? skillConfig.retrieval : {};
  const profileRaw = String(raw.profile || DEFAULT_FAQ_RETRIEVAL.profile).toLowerCase();
  const profile = FAQ_RETRIEVAL_PROFILES[profileRaw] ? profileRaw : "balanced";
  const preset = FAQ_RETRIEVAL_PROFILES[profile];
  return {
    profile,
    top_k: Number.isFinite(Number(raw.top_k)) ? Number(raw.top_k) : preset.top_k,
    min_lexical_score: Number.isFinite(Number(raw.min_lexical_score))
      ? Number(raw.min_lexical_score)
      : preset.min_lexical_score,
    alias_groups: Array.isArray(raw.alias_groups) ? raw.alias_groups : [],
    alias_groups_mode: raw.alias_groups_mode === "replace" ? "replace" : "merge",
    /** true se o JSON já tinha retrieval persistido */
    persisted: Boolean(skillConfig?.retrieval && typeof skillConfig.retrieval === "object"),
  };
}

export function getEffectiveFaqRouting(skillConfig = {}) {
  const raw = skillConfig?.routing && typeof skillConfig.routing === "object" ? skillConfig.routing : {};
  const rules = Array.isArray(raw.action_rules) ? raw.action_rules : [];
  return {
    action_rules: rules,
    redirect_fallback_reply:
      typeof raw.redirect_fallback_reply === "string" ? raw.redirect_fallback_reply : "",
    persisted: Boolean(skillConfig?.routing && typeof skillConfig.routing === "object"),
  };
}

/** Aplica perfil (só quando o operador clica — nunca no load). */
export function buildRetrievalFromProfile(profileName) {
  const profile = FAQ_RETRIEVAL_PROFILES[profileName] ? profileName : "balanced";
  const preset = FAQ_RETRIEVAL_PROFILES[profile];
  return {
    profile,
    top_k: preset.top_k,
    min_lexical_score: preset.min_lexical_score,
    alias_groups_mode: "merge",
  };
}

/**
 * Qualidade agregada da base (soft gate).
 * @returns {"ok"|"low"|"empty"|"unknown"}
 */
export function aggregateKnowledgeQuality(knowledgePreviews = {}, { documents = [], sourceUrls = [] } = {}) {
  const hasSources = (documents?.length || 0) > 0 || (sourceUrls?.length || 0) > 0;
  if (!hasSources) return "empty";
  const values = Object.values(knowledgePreviews || {});
  if (!values.length) return "unknown";
  if (values.some((p) => p?.quality === "empty" || p?.quality === "error")) {
    if (values.every((p) => p?.quality === "empty" || p?.quality === "error")) return "empty";
    return "low";
  }
  if (values.some((p) => p?.quality === "low")) return "low";
  if (values.some((p) => p?.quality === "ok")) return "ok";
  return "unknown";
}

export function normalizeFaqSkillConfig(config = {}) {
  return {
    greeting_message: config.greeting_message ?? "",
    greeting_message_morning: config.greeting_message_morning ?? "",
    greeting_message_afternoon: config.greeting_message_afternoon ?? "",
    greeting_message_evening: config.greeting_message_evening ?? "",
    assistant_identity: config.assistant_identity ?? "",
    no_match_reply: config.no_match_reply ?? "",
    avoid_closing_questions: config.avoid_closing_questions === true,
    transfer_offer_hint: config.transfer_offer_hint ?? "",
    faq_answer_focus: config.faq_answer_focus ?? "",
    outside_hours_message: getFaqOutsideHoursMessage(config),
    business_hours: config.business_hours ?? null,
    retrieval: config.retrieval ?? undefined,
    routing: config.routing ?? undefined,
  };
}
